import React, { useRef, useState, useMemo } from 'react';
import { Animated, View, StyleSheet } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import createStyleSheets from 'utils/createStyleSheets';
import { isWeb } from '../../../constants';
const HORIZONTAL_PADDING = rem(80);
const PERIOD_WIDTH = rem(60);
const PERIOD_HEIGHT = rem(28);
const PERIODS = ['Equity', 'P/L'];
const hitSlop = { top: 10, bottom: 10, left: 10, right: 10 };
const INACTIVE_OPACITY = 0;
const ModeSwitch = ({ onSelect, defaultPeriod }) => {
    const { width } = useAppFrameDimensions(isWeb);
    const POSITIONS = useMemo(() => PERIODS.map((_p, i) => ({
        top: 0,
        left: HORIZONTAL_PADDING -
            rem(4) +
            ((width - HORIZONTAL_PADDING * 2) * (i + 1)) / (PERIODS.length + 1) -
            PERIOD_WIDTH / 2,
    })), [width]);
    const [selected, setSelected] = useState(defaultPeriod);
    const animatedOpacity = useRef(PERIODS.map((period) => new Animated.Value(period === selected ? 1 : INACTIVE_OPACITY)));
    const animatedLeft = useRef(new Animated.Value(POSITIONS[PERIODS.findIndex((p) => p === selected)].left));
    const animatedScaleX = useRef(new Animated.Value(1));
    const styles = useStyles(styleSet);
    return (<View style={styles.container}>
      <Animated.View style={[
            styles.selectedPeriodBackground,
            {
                transform: [{ translateX: animatedLeft.current }, { scaleX: animatedScaleX.current }],
            },
        ]}/>
      {PERIODS.map((period, index) => (<TouchableBounce key={period} style={[styles.period, POSITIONS[index]]} onPress={() => {
                setSelected(period);
                Animated.parallel([
                    ...animatedOpacity.current.map((value, index) => Animated.timing(value, {
                        toValue: period === PERIODS[index] ? 1 : INACTIVE_OPACITY,
                        duration: 300,
                        useNativeDriver: true,
                    })),
                    Animated.timing(animatedLeft.current, {
                        toValue: POSITIONS[index].left,
                        duration: 300,
                        useNativeDriver: true,
                    }),
                    Animated.timing(animatedScaleX.current, {
                        toValue: index === PERIODS.length - 1 ? 1.2 : 1,
                        duration: 300,
                        useNativeDriver: true,
                    }),
                ]).start();
                onSelect(period);
            }} hitSlop={hitSlop}>
          <View style={styles.periodContainer}>
            <Animated.View style={{ opacity: animatedOpacity.current[index] }}>
              <Text Text-12 White>
                {period}
              </Text>
            </Animated.View>
            <Animated.View style={[
                styles.overlay,
                {
                    opacity: animatedOpacity.current[index].interpolate({
                        inputRange: [0, 1],
                        outputRange: [1, 0],
                    }),
                },
            ]}>
              <View>
                <Text Text-12 Gray>
                  {period}
                </Text>
              </View>
            </Animated.View>
          </View>
        </TouchableBounce>))}
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        width: '100%',
        height: PERIOD_HEIGHT,
    },
    periodContainer: {
        width: PERIOD_WIDTH,
        height: PERIOD_HEIGHT,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: rem(12),
    },
    period: {
        position: 'absolute',
        borderRadius: rem(12),
    },
    selectedPeriodBackground: {
        position: 'absolute',
        width: PERIOD_WIDTH,
        height: PERIOD_HEIGHT,
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        borderRadius: rem(12),
        backgroundColor: colors.background.brand,
    },
    overlay: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: rem(12),
    },
}));
export default ModeSwitch;
